import React, { useState } from 'react';
import { Modal, Button } from './ui';
import { HardDrive, Mail, Check } from 'lucide-react';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';

// BuyStorageModal
// ─────────────────────────────────────────────────────────────────────
// Customer-facing storage add-on picker. Shows the firm admin the
// preset tiers and the GHS price for each, then routes to the
// purchase flow.
//
// PURCHASE FLOW (v1 — manual + automated webhook):
//   1. Customer picks a tier and clicks "Request this upgrade"
//   2. We open a pre-filled mailto: to billing support with the
//      firm name + chosen tier + firm_id in the body
//   3. The DocketFlow billing operator creates a Paystack one-time
//      charge with metadata { addon_type: "storage", addon_gb: N,
//      firm_id: <firm-uuid> } and sends the payment link
//   4. Customer pays; Paystack webhook fires
//   5. Migration-143 RPC `add_firm_storage_addon` stacks the GB
//      onto firms.storage_addon_mb — the customer's available
//      storage updates in real time after the webhook lands
//
// Why not in-app Paystack checkout yet:
//   Paystack inline JS (`PaystackPop.setup(...)`) requires the
//   public key, a deployed checkout URL, and Paystack live-mode
//   product SKUs that aren't set up yet. PAYSTACK_SETUP.md
//   describes the steps. Until then, the manual-handoff flow
//   gives customers a clear path while the webhook side does the
//   stacking automatically once the operator processes payment.
//
// Props:
//   open           — boolean
//   onClose        — fn
//   currentAddonMb — number (MB), shown so the customer sees what
//                    they've already purchased and won't second-
//                    guess the new amount

const STORAGE_TIERS = [
  { gb: 10,   priceGhs: 25,   label: '10 GB' },
  { gb: 50,   priceGhs: 100,  label: '50 GB',  badge: 'Best value' },
  { gb: 200,  priceGhs: 300,  label: '200 GB' },
  { gb: 1024, priceGhs: 1200, label: '1 TB' },
];

const SUPPORT_EMAIL = 'billing@docketflowapp.com';

function formatGb(gb) {
  return gb >= 1024 ? '1 TB' : `${gb} GB`;
}

function formatPriceGhs(p) {
  return `GH₵ ${p.toLocaleString('en-GH')}`;
}

function pricePerGb(tier) {
  return tier.priceGhs / tier.gb;
}

export default function BuyStorageModal({ open, onClose, currentAddonMb = 0 }) {
  const { firm } = useFirm();
  const { user } = useAuth();
  const [selectedGb, setSelectedGb] = useState(50);
  const [sent, setSent] = useState(false);

  const selectedTier = STORAGE_TIERS.find((t) => t.gb === selectedGb) || STORAGE_TIERS[1];
  const currentAddonGb = (currentAddonMb || 0) / 1024;

  const handleRequest = () => {
    // Construct a mailto: with all the info the billing operator
    // needs to create the Paystack charge — name, email, firm_id,
    // GB amount, price. Avoids back-and-forth.
    const subject = encodeURIComponent(
      `[Storage upgrade] ${formatGb(selectedTier.gb)} for ${firm?.name || 'firm'}`,
    );
    const body = encodeURIComponent([
      `Hi,`,
      ``,
      `Please send me a payment link for an additional ${formatGb(selectedTier.gb)} of DocketFlow storage (${formatPriceGhs(selectedTier.priceGhs)}).`,
      ``,
      `Firm: ${firm?.name || ''}`,
      `Firm ID: ${firm?.id || ''}`,
      `Requested by: ${user?.email || ''}`,
      `Current add-on: ${currentAddonGb.toFixed(1)} GB`,
      `Add-on requested: ${formatGb(selectedTier.gb)}`,
      `Price: ${formatPriceGhs(selectedTier.priceGhs)}`,
      ``,
      `Thanks!`,
    ].join('\n'));

    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Buy more storage"
      size="lg"
      footer={
        sent ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button icon={Mail} onClick={handleRequest}>
              Request {formatGb(selectedTier.gb)} — {formatPriceGhs(selectedTier.priceGhs)}
            </Button>
          </>
        )
      }
    >
      {sent ? (
        // Confirmation state — after they hit "Request", surface what
        // happens next so they're not left wondering.
        <div className="py-2 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Check size={20} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">Email opened</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Send the message to <span className="font-mono">{SUPPORT_EMAIL}</span> and our billing team will reply with a payment link within one business day.
              </p>
            </div>
          </div>
          <div className="rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 px-4 py-3 text-sm text-indigo-900 dark:text-indigo-200">
            Once you complete payment, your storage will update automatically — no further action needed on your side.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Add-ons stack on top of your current plan. One-time purchase, no recurring charges.
            {currentAddonGb > 0 && (
              <> You've already purchased <span className="font-semibold">{currentAddonGb.toFixed(1)} GB</span> in add-ons.</>
            )}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {STORAGE_TIERS.map((tier) => {
              const isSelected = tier.gb === selectedGb;
              return (
                <button
                  key={tier.gb}
                  onClick={() => setSelectedGb(tier.gb)}
                  className={`relative text-left p-4 rounded-xl border-2 transition ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  {tier.badge && (
                    <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-600 text-white">
                      {tier.badge}
                    </span>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <HardDrive size={16} className={isSelected ? 'text-indigo-600' : 'text-slate-400'} />
                      <span className="font-semibold text-base text-slate-900 dark:text-white">
                        {tier.label}
                      </span>
                    </div>
                    {isSelected && <Check size={16} className="text-indigo-600 flex-shrink-0" />}
                  </div>
                  <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                    {formatPriceGhs(tier.priceGhs)}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {(pricePerGb(tier) * 100 / 100).toFixed(2)} GHS / GB · one-time
                  </p>
                </button>
              );
            })}
          </div>

          <div className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 text-xs text-slate-600 dark:text-slate-400 space-y-1">
            <p>
              <strong className="text-slate-700 dark:text-slate-300">How it works:</strong>{' '}
              You'll request the upgrade by email. Our billing team sends a Paystack payment link.
              Once paid, your storage is increased instantly — no support follow-up required.
            </p>
            <p className="text-slate-500 dark:text-slate-500">
              Need a custom amount or annual prepay? Just include it in the email.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}
